'use client';

import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Github, Linkedin } from 'lucide-react';
import { getAiAmpUrl, getMarketingUrl } from '@lykbl/config';
import { BrandLogo, type SiteContext } from './BrandLogo';

// Site-aware footer links generator (using functions for dynamic URLs)
const getFooterLinks = (site: SiteContext) => {
  const aiAmpUrl = getAiAmpUrl('');
  const marketingUrl = getMarketingUrl('');
  
  return {
    product: [
      { label: 'Ai-AMP Marketplace', href: site === 'ai-amp' ? '/' : `${aiAmpUrl}/`, internal: site === 'ai-amp' },
      { label: 'For Creators', href: site === 'marketing' ? '/creators' : `${marketingUrl}/creators`, internal: site === 'marketing' },
      { label: 'For Brands', href: site === 'marketing' ? '/brands' : `${marketingUrl}/brands`, internal: site === 'marketing' },
      { label: 'For Developers', href: site === 'ai-amp' ? '/developers' : `${aiAmpUrl}/developers`, internal: site === 'ai-amp' },
    ],
    resources: [
      { label: 'Documentation', href: site === 'ai-amp' ? '/docs' : `${aiAmpUrl}/docs`, internal: site === 'ai-amp' },
      { label: 'Blog', href: site === 'marketing' ? '/blog' : `${marketingUrl}/blog`, internal: site === 'marketing' },
      { label: 'Help Center', href: site === 'marketing' ? '/help' : `${marketingUrl}/help`, internal: site === 'marketing' },
    ],
    company: [
      { label: 'About', href: site === 'marketing' ? '/about' : `${marketingUrl}/about`, internal: site === 'marketing' },
    ],
    legal: [
      { label: 'Terms of Service', href: '/legal/terms', internal: true },
      // Ai-AMP uses /legal/privacy, marketing uses /privacy
      { label: 'Privacy Policy', href: site === 'ai-amp' ? '/legal/privacy' : '/privacy', internal: true },
    ],
  };
};

const SOCIAL_LINKS = [
  { icon: Twitter, href: 'https://twitter.com/lykbl', label: 'Twitter' },
  { icon: Github, href: 'https://github.com/lykbl', label: 'GitHub' },
  { icon: Linkedin, href: 'https://linkedin.com/company/lykbl', label: 'LinkedIn' },
];

interface FooterProps {
  site: SiteContext;
  className?: string;
}

function FooterComponent({ site, className = '' }: FooterProps) {
  const accentColor = site === 'ai-amp' ? '#00D4FF' : '#FF6B6B';
  const year = new Date().getFullYear();
  const FOOTER_LINKS = useMemo(() => getFooterLinks(site), [site]);
  const currentDomain = site === 'ai-amp' ? getAiAmpUrl('') : getMarketingUrl('');
  const displayDomain = currentDomain.replace('https://', '');

  const renderLink = (link: { label: string; href: string; internal?: boolean }) => {
    if (link.internal) {
      return (
        <Link
          key={link.label}
          to={link.href}
          className="text-[#A8A8B3] hover:text-white transition-colors text-sm"
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.label}
        href={link.href}
        className="text-[#A8A8B3] hover:text-white transition-colors text-sm"
      >
        {link.label}
      </a>
    );
  };

  return (
    <footer className={`bg-ink border-t border-white/5 ${className}`}>
      <div className="container py-12 md:py-16">
        {/* Main Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <BrandLogo variant="light" size="md" />
            <p className="mt-4 text-sm text-[#A8A8B3] leading-relaxed max-w-xs">
              The operating system for modern content creators. Powered by Ai Agents.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-[#A8A8B3] hover:text-white hover:bg-white/10 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Product</h3>
            <div className="flex flex-col gap-3">
              {FOOTER_LINKS.product.map(renderLink)}
            </div>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Resources</h3>
            <div className="flex flex-col gap-3">
              {FOOTER_LINKS.resources.map(renderLink)}
            </div>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Company</h3>
            <div className="flex flex-col gap-3">
              {FOOTER_LINKS.company.map(renderLink)}
            </div>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Legal</h3>
            <div className="flex flex-col gap-3">
              {FOOTER_LINKS.legal.map(renderLink)}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#A8A8B3]">
            © {year} lykbl™. All rights reserved.
          </p>
          
          <div className="flex items-center gap-2 text-xs text-[#A8A8B3]">
            <span>You're on</span>
            <span
              className="px-2 py-0.5 rounded-full border"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}30`,
                color: accentColor,
              }}
            >
              {displayDomain}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export const Footer = memo(FooterComponent);
